import React, { Fragment, useEffect, useState } from 'react';
import { Offcanvas, Accordion } from 'react-bootstrap';

export const Config = ({ show, handleClose, colors, setColors, setTheme, theme }) => {

    const [customColors, setCustomColors] = useState({});

    // 🎨 PALETA TEMA CLARO (Light)
    const lightThemeColors = {
        colorBg: '#ffffff',
        colorFont: '#212529',
        
        ldColorAlarm: '#dc3545',
        ldColorBg: '#f8f9fa',
        ldColorBorder: '#dee2e6',
        ldColorFont: '#212529',
        ldColorMeasure: '#1a5490',

        grColorBg: '#ffffff',
        grColorAlarm: '#dc3545',
        grColorBorder: '#dee2e6',
        grColorFont: '#212529',
        grColorMeasure: '#1a5490',
        grColor1_1: '#E74C3C',
        grColor1_2: '#16A085',
        grColor2_1: '#2980B9',
        grColor2_2: '#27AE60',
        grColor3_1: '#F39C12',
        grColor3_2: '#8E44AD',
        grColor4_1: '#1ABC9C',
        grColor4_2: '#D68910',
        grColor5_1: '#6C3483',
        grColor5_2: '#2471A3',
    };

    // 🎨 PALETA TEMA OSCURO (Dark)
    const darkThemeColors = {
        colorBg: '#000000',
        colorFont: '#ffffff',

        ldColorAlarm: '#ff6b6b',
        ldColorBg: '#0a0a0a',
        ldColorBorder: '#333333',
        ldColorFont: '#ffffff',
        ldColorMeasure: '#45B7D1',

        grColorBg: '#000000',
        grColorAlarm: '#ff6b6b',
        grColorBorder: '#333333',
        grColorFont: '#ffffff',
        grColorMeasure: '#45B7D1',
        grColor1_1: '#FF6B6B',
        grColor1_2: '#4ECDC4',
        grColor2_1: '#45B7D1',
        grColor2_2: '#96CEB4',
        grColor3_1: '#FFEAA7',
        grColor3_2: '#DDA0DD',
        grColor4_1: '#98D8C8',
        grColor4_2: '#F7DC6F',
        grColor5_1: '#BB8FCE',
        grColor5_2: '#85C1E9',
    };

    // 🎨 PALETA TEMA CUSTOM (Inicial - por defecto usa los colores del tema oscuro)
    const initialCustomColors = {
        colorBg: '#000000',
        colorFont: '#ffffff',

        ldColorAlarm: '#ff6b6b',
        ldColorBg: '#0a0a0a',
        ldColorBorder: '#333333',
        ldColorFont: '#ffffff',
        ldColorMeasure: '#45B7D1',

        grColorBg: '#000000',
        grColorAlarm: '#ff6b6b',
        grColorBorder: '#333333',
        grColorFont: '#ffffff',
        grColorMeasure: '#45B7D1',
        grColor1_1: '#FF6B6B',
        grColor1_2: '#4ECDC4',
        grColor2_1: '#45B7D1',
        grColor2_2: '#96CEB4',
        grColor3_1: '#FFEAA7',
        grColor3_2: '#DDA0DD',
        grColor4_1: '#98D8C8',
        grColor4_2: '#F7DC6F',
        grColor5_1: '#BB8FCE',
        grColor5_2: '#85C1E9',
    };

    // 🔄 Cargar colores guardados al montar
    useEffect(() => {
        const storedCustomColors = JSON.parse(localStorage.getItem('colorsCustom')) || initialCustomColors;
        setCustomColors(storedCustomColors);
        
        // Aplicar colores según el tema actual
        const currentTheme = localStorage.getItem('theme') || 'dark';
        applyThemeColors(currentTheme);
    }, []);

    // 🎨 Aplicar colores según el tema seleccionado
    const applyThemeColors = (selectedTheme) => {
        let themePalette;
        
        switch(selectedTheme) {
            case 'light':
                themePalette = lightThemeColors;
                break;
            case 'dark':
                themePalette = darkThemeColors;
                break;
            case 'custom':
                themePalette = customColors;
                break;
            default:
                themePalette = darkThemeColors;
        }
        
        setColors(themePalette);
        localStorage.setItem('colors', JSON.stringify(themePalette));
    };

    // 🔄 Cambiar tema y aplicar colores correspondientes
    const handleThemeChange = (newTheme) => {
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
        applyThemeColors(newTheme);
        
        // Disparar evento para que otros componentes se enteren
        document.documentElement.setAttribute('data-theme', newTheme);
        window.dispatchEvent(new Event('themeChanged'));
    };

    // 🎨 Cambiar color individual en tema custom
    const handleCustomColorChange = (key, value) => {
        const newCustomColors = { ...customColors, [key]: value };
        setCustomColors(newCustomColors);
        localStorage.setItem('colorsCustom', JSON.stringify(newCustomColors));
        
        // Si estamos en tema custom, actualizar inmediatamente
        if (theme === 'custom') {
            setColors(newCustomColors);
            localStorage.setItem('colors', JSON.stringify(newCustomColors));
            
            // Disparar evento para que otros componentes se actualicen
            window.dispatchEvent(new Event('themeChanged'));
        }
    };

    // 🔄 Resetear tema custom a valores por defecto
    const handleResetCustom = () => {
        setCustomColors(initialCustomColors);
        localStorage.setItem('colorsCustom', JSON.stringify(initialCustomColors));
        
        if (theme === 'custom') {
            setColors(initialCustomColors);
            localStorage.setItem('colors', JSON.stringify(initialCustomColors));
            
            // Disparar evento para que otros componentes se actualicen
            window.dispatchEvent(new Event('themeChanged'));
        }
    };

    // 🎨 Componente para mostrar paleta de colores (preview)
    const ColorPalettePreview = ({ themeColors, themeName }) => {
        return (
            <div style={{
                padding: '15px',
                borderRadius: '8px',
                backgroundColor: themeColors.colorBg,
                border: `2px solid ${themeColors.grColorBorder}`,
                marginBottom: '10px'
            }}>
                <h6 style={{ color: themeColors.colorFont, marginBottom: '10px', textAlign: 'center' }}>
                    {themeName}
                </h6>
                
                {/* Colores principales */}
                <div style={{ marginBottom: '8px' }}>
                    <small style={{ color: themeColors.colorFont, display: 'block', marginBottom: '5px' }}>
                        <i className="fa fa-chart-line me-2" />Líneas de gráficas:
                    </small>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        {[1, 2, 3, 4, 5].map(track => (
                            <Fragment key={track}>
                                <div style={{
                                    width: '35px',
                                    height: '25px',
                                    backgroundColor: themeColors[`grColor${track}_1`],
                                    borderRadius: '4px',
                                    border: '1px solid rgba(0,0,0,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    color: '#fff',
                                    textShadow: '0 0 2px #000'
                                }}>
                                    {track}-1
                                </div>
                                <div style={{
                                    width: '35px',
                                    height: '25px',
                                    backgroundColor: themeColors[`grColor${track}_2`],
                                    borderRadius: '4px',
                                    border: '1px solid rgba(0,0,0,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    color: '#fff',
                                    textShadow: '0 0 2px #000'
                                }}>
                                    {track}-2
                                </div>
                            </Fragment>
                        ))}
                    </div>
                </div>

                {/* Colores de medición y alarma */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                        <small style={{ color: themeColors.colorFont, display: 'block', fontSize: '10px' }}>
                            Medida
                        </small>
                        <div style={{
                            width: '100%',
                            height: '30px',
                            backgroundColor: themeColors.ldColorMeasure,
                            borderRadius: '4px',
                            border: '1px solid rgba(0,0,0,0.2)',
                            marginTop: '3px'
                        }} />
                    </div>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                        <small style={{ color: themeColors.colorFont, display: 'block', fontSize: '10px' }}>
                            Alarma
                        </small>
                        <div style={{
                            width: '100%',
                            height: '30px',
                            backgroundColor: themeColors.ldColorAlarm,
                            borderRadius: '4px',
                            border: '1px solid rgba(0,0,0,0.2)',
                            marginTop: '3px'
                        }} />
                    </div>
                </div>
            </div>
        );
    };

    const trItemCanvas = (texto, icono, colorKey, unir) => {
        return (
            <tr>
                <td style={{ width: '30px' }}>
                    <i className={`${icono} text-center`}
                        style={{ color: colors.colorFont || '#000000' }} />
                </td>
                <td colSpan={unir ? 2 : 1}>{texto}</td>
                <td className='text-end' style={{ width: '80px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        <code style={{ 
                            fontSize: '10px', 
                            color: colors.colorFont,
                            opacity: 0.7 
                        }}>
                            {customColors[colorKey]}
                        </code>
                        <input
                            type="color"
                            className="color-picker border-0"
                            style={{ width: '30px', height: '30px', cursor: 'pointer', borderRadius: '4px' }}
                            value={customColors[colorKey]}
                            onChange={(e) => handleCustomColorChange(colorKey, e.target.value)}
                        />
                    </div>
                </td>
            </tr>
        );
    };

    const tr2ItemCanvas = (track, texto1, texto2, icono, colorKey1, colorKey2) => {
        return (
            <Fragment>
                <tr>
                    <td rowSpan={2} style={{ width: '30px' }}>
                        <i className={`${icono} text-center`}
                            style={{ color: colors.colorFont || '#000000' }} />
                    </td>
                    <td rowSpan={2} style={{ fontWeight: '600' }}>{track}</td>
                    <td>{texto1}</td>
                    <td className='text-end' style={{ width: '80px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                            <code style={{ 
                                fontSize: '10px', 
                                color: colors.colorFont,
                                opacity: 0.7 
                            }}>
                                {customColors[colorKey1]}
                            </code>
                            <input
                                type="color"
                                style={{ width: '30px', height: '30px', cursor: 'pointer', borderRadius: '4px' }}
                                className="color-picker border-0"
                                value={customColors[colorKey1]}
                                onChange={(e) => handleCustomColorChange(colorKey1, e.target.value)}
                            />
                        </div>
                    </td>
                </tr>
                <tr>
                    <td>{texto2}</td>
                    <td className='text-end'>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                            <code style={{ 
                                fontSize: '10px', 
                                color: colors.colorFont,
                                opacity: 0.7 
                            }}>
                                {customColors[colorKey2]}
                            </code>
                            <input
                                type="color"
                                style={{ width: '30px', height: '30px', cursor: 'pointer', borderRadius: '4px' }}
                                className="color-picker border-0"
                                value={customColors[colorKey2]}
                                onChange={(e) => handleCustomColorChange(colorKey2, e.target.value)}
                            />
                        </div>
                    </td>
                </tr>
            </Fragment>
        );
    };

    const tableCanvas = (titulo, contenido, unir) => {
        return (
            <table className="table table-sm table-hover align-middle" 
                style={{ 
                    color: colors.colorFont || '#000000', 
                    fontSize: '12px',
                    marginBottom: '10px'
                }}>
                <thead>
                    <tr>
                        <th colSpan={unir ? 4 : 4} className='text-center' 
                            style={{ 
                                backgroundColor: 'rgba(78, 205, 196, 0.1)',
                                borderBottom: '2px solid rgba(78, 205, 196, 0.3)',
                                padding: '10px'
                            }}>
                            {titulo}
                        </th>
                    </tr>
                </thead>
                <tbody> {contenido} </tbody>
            </table>
        );
    }

    const tdButtonTheme = (th, icono, titulo, themeColors) => {
        const isActive = theme === th;
        return (
            <div style={{ flex: 1, padding: '5px' }}>
                <button
                    className={`btn btn-${isActive ? 'primary' : 'outline-secondary'} w-100`}
                    onClick={() => handleThemeChange(th)}
                    style={{
                        padding: '15px 10px',
                        borderRadius: '8px',
                        border: isActive ? '2px solid #4ECDC4' : '2px solid rgba(0,0,0,0.1)',
                        backgroundColor: isActive ? 'rgba(78, 205, 196, 0.2)' : 'transparent',
                        transition: 'all 0.3s ease'
                    }}>
                    <div>
                        <i className={`${icono} mb-2`} style={{ fontSize: '24px' }} />
                        <div style={{ fontSize: '14px', fontWeight: '600' }}>{titulo}</div>
                    </div>
                </button>
                
                {/* Preview de paleta */}
                {themeColors && (
                    <div style={{ marginTop: '10px' }}>
                        <div style={{ 
                            display: 'flex', 
                            gap: '3px', 
                            justifyContent: 'center',
                            padding: '8px',
                            backgroundColor: 'rgba(0,0,0,0.05)',
                            borderRadius: '6px'
                        }}>
                            {[1, 2, 3, 4, 5].map(track => (
                                <div key={track} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: themeColors[`grColor${track}_1`],
                                        borderRadius: '3px',
                                        border: '1px solid rgba(0,0,0,0.1)'
                                    }} />
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: themeColors[`grColor${track}_2`],
                                        borderRadius: '3px',
                                        border: '1px solid rgba(0,0,0,0.1)'
                                    }} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end"
            style={{
                color: theme === 'custom' ? customColors.colorFont : colors.colorFont || '#000000',
                backgroundColor: theme === 'dark' ? '#0b2948' : theme === 'light' ? '#f8f9fa' : customColors.colorBg || '#FFFFFF',
                border: `1px solid ${colors.colorFont || '#000000'}`,
                width: '420px'
            }}>
            <Offcanvas.Header style={{ 
                borderBottom: '2px solid rgba(78, 205, 196, 0.3)',
                paddingBottom: '15px'
            }}>
                <Offcanvas.Title>
                    <i className="fa-solid fa-palette me-2" style={{ color: '#4ECDC4' }} />
                    Configuración de Temas
                    <small style={{ display: 'block', fontSize: '12px', opacity: 0.7, marginTop: '5px' }}>
                        Tema actual: <strong>{theme === 'dark' ? 'Oscuro' : theme === 'light' ? 'Claro' : 'Personalizado'}</strong>
                    </small>
                </Offcanvas.Title>
                <button type="button"
                    className="btn fa-solid fa-times"
                    onClick={handleClose}
                    aria-label="Close"
                    style={{ color: colors.colorFont || '#000000', fontSize: '20px' }} />
            </Offcanvas.Header>
            <Offcanvas.Body className="p-3" style={{ overflowY: 'auto' }}>
                
                {/* SELECTOR DE TEMAS */}
                <div style={{ 
                    marginBottom: '20px',
                    padding: '15px',
                    backgroundColor: 'rgba(78, 205, 196, 0.05)',
                    borderRadius: '10px',
                    border: '1px solid rgba(78, 205, 196, 0.2)'
                }}>
                    <h6 style={{ 
                        marginBottom: '15px', 
                        textAlign: 'center',
                        color: '#4ECDC4',
                        fontWeight: '600'
                    }}>
                        <i className="fa-solid fa-swatchbook me-2" />
                        Seleccionar Tema
                    </h6>
                    
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                        {tdButtonTheme('dark', 'fa-solid fa-moon', 'Oscuro', darkThemeColors)}
                        {tdButtonTheme('light', 'fa-solid fa-sun', 'Claro', lightThemeColors)}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {tdButtonTheme('custom', 'fa-solid fa-palette', 'Personalizado', customColors)}
                        {theme === 'custom' && (
                            <div style={{ padding: '5px' }}>
                                <button
                                    className="btn btn-outline-danger w-100"
                                    onClick={handleResetCustom}
                                    style={{
                                        padding: '15px 10px',
                                        borderRadius: '8px',
                                        height: '100%'
                                    }}
                                    title="Resetear tema custom">
                                    <i className="fa-solid fa-undo me-2" />
                                    <div style={{ fontSize: '12px' }}>Resetear</div>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* PREVIEW DE PALETAS PARA DARK Y LIGHT */}
                {(theme === 'dark' || theme === 'light') && (
                    <div style={{ marginBottom: '20px' }}>
                        <h6 style={{ 
                            marginBottom: '15px',
                            color: '#4ECDC4',
                            fontWeight: '600',
                            textAlign: 'center'
                        }}>
                            <i className="fa-solid fa-eye me-2" />
                            Vista Previa de Paleta
                        </h6>
                        <ColorPalettePreview 
                            themeColors={theme === 'dark' ? darkThemeColors : lightThemeColors}
                            themeName={theme === 'dark' ? 'Tema Oscuro' : 'Tema Claro'}
                        />
                    </div>
                )}

                {/* EDITOR CUSTOM */}
                {theme === 'custom' && (
                    <div>
                        <h6 style={{ 
                            marginBottom: '15px',
                            color: '#4ECDC4',
                            fontWeight: '600',
                            textAlign: 'center'
                        }}>
                            <i className="fa-solid fa-paint-brush me-2" />
                            Personalizar Colores
                        </h6>

                        {/* Preview del tema custom */}
                        <ColorPalettePreview 
                            themeColors={customColors}
                            themeName="Tu Tema Personalizado"
                        />

                        {/* Accordion para organizar secciones */}
                        <Accordion defaultActiveKey="0" style={{ marginTop: '15px' }}>
                            
                            {/* SECCIÓN: General */}
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>
                                    <i className="fa-solid fa-cog me-2" style={{ color: '#4ECDC4' }} />
                                    <strong>General</strong>
                                </Accordion.Header>
                                <Accordion.Body style={{ padding: '10px' }}>
                                    {tableCanvas('Colores Generales',
                                        <Fragment>
                                            {trItemCanvas('Fondo Principal', 'fa fa-square', 'colorBg')}
                                            {trItemCanvas('Texto Principal', 'fa fa-font', 'colorFont')}
                                        </Fragment>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>

                            {/* SECCIÓN: Live Data */}
                            <Accordion.Item eventKey="1">
                                <Accordion.Header>
                                    <i className="fa-solid fa-tachometer-alt me-2" style={{ color: '#4ECDC4' }} />
                                    <strong>Live Data</strong>
                                </Accordion.Header>
                                <Accordion.Body style={{ padding: '10px' }}>
                                    {tableCanvas('Colores de Tarjetas',
                                        <Fragment>
                                            {trItemCanvas('Fondo de Tarjeta', 'fa fa-square', 'ldColorBg')}
                                            {trItemCanvas('Borde de Tarjeta', 'fa-solid fa-border-all', 'ldColorBorder')}
                                            {trItemCanvas('Texto de Tarjeta', 'fa fa-font', 'ldColorFont')}
                                            {trItemCanvas('Color de Medida', 'fa-solid fa-1', 'ldColorMeasure')}
                                            {trItemCanvas('Color de Alarma', 'fa-solid fa-exclamation-triangle', 'ldColorAlarm')}
                                        </Fragment>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>

                            {/* SECCIÓN: Gráficas - Generales */}
                            <Accordion.Item eventKey="2">
                                <Accordion.Header>
                                    <i className="fa-solid fa-chart-area me-2" style={{ color: '#4ECDC4' }} />
                                    <strong>Gráficas - General</strong>
                                </Accordion.Header>
                                <Accordion.Body style={{ padding: '10px' }}>
                                    {tableCanvas('Colores Generales de Gráficas',
                                        <Fragment>
                                            {trItemCanvas('Fondo de Gráfica', 'fa fa-square', 'grColorBg', true)}
                                            {trItemCanvas('Borde de Gráfica', 'fa-solid fa-border-all', 'grColorBorder', true)}
                                            {trItemCanvas('Texto de Gráfica', 'fa fa-font', 'grColorFont', true)}
                                            {trItemCanvas('Color de Medida', 'fa-solid fa-1', 'grColorMeasure', true)}
                                            {trItemCanvas('Color de Alarma', 'fa-solid fa-exclamation-triangle', 'grColorAlarm', true)}
                                        </Fragment>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>

                            {/* SECCIÓN: Tracks (Líneas) */}
                            <Accordion.Item eventKey="3">
                                <Accordion.Header>
                                    <i className="fa-solid fa-chart-line me-2" style={{ color: '#4ECDC4' }} />
                                    <strong>Líneas de Gráficas (Tracks)</strong>
                                </Accordion.Header>
                                <Accordion.Body style={{ padding: '10px' }}>
                                    {tableCanvas('Colores de Líneas por Track',
                                        <Fragment>
                                            {tr2ItemCanvas('Track 1', 'Línea 1', 'Línea 2', 'fa-solid fa-chart-line', 'grColor1_1', 'grColor1_2')}
                                            {tr2ItemCanvas('Track 2', 'Línea 1', 'Línea 2', 'fa-solid fa-chart-line', 'grColor2_1', 'grColor2_2')}
                                            {tr2ItemCanvas('Track 3', 'Línea 1', 'Línea 2', 'fa-solid fa-chart-line', 'grColor3_1', 'grColor3_2')}
                                            {tr2ItemCanvas('Track 4', 'Línea 1', 'Línea 2', 'fa-solid fa-chart-line', 'grColor4_1', 'grColor4_2')}
                                            {tr2ItemCanvas('Track 5', 'Línea 1', 'Línea 2', 'fa-solid fa-chart-line', 'grColor5_1', 'grColor5_2')}
                                        </Fragment>, true
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>

                        </Accordion>

                        {/* Botón para aplicar cambios */}
                        <div style={{ 
                            marginTop: '20px', 
                            padding: '15px',
                            backgroundColor: 'rgba(78, 205, 196, 0.1)',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            <small style={{ display: 'block', marginBottom: '10px', opacity: 0.8 }}>
                                <i className="fa-solid fa-info-circle me-2" />
                                Los cambios se aplican automáticamente
                            </small>
                        </div>
                    </div>
                )}

            </Offcanvas.Body>
        </Offcanvas>
    );
};